import React, { useEffect, useState, useRef } from 'react';
import { Check, ChevronDown } from 'lucide-react';
interface MultiSelectDropdownProps<T extends string> {
  options: {
    label: string;
    value: T;
  }[];
  selectedValues: Set<T>;
  onChange: (values: Set<T>) => void;
  placeholder: string;
  icon?: React.ReactNode;
  className?: string;
}
export function MultiSelectDropdown<T extends string>({
  options,
  selectedValues,
  onChange,
  placeholder,
  icon,
  className = ''
}: MultiSelectDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggleOption = (value: T) => {
    const newSelected = new Set(selectedValues);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    onChange(newSelected);
  };
  const displayValue = selectedValues.size === 0 ? placeholder : selectedValues.size === 1 ? options.find(o => o.value === Array.from(selectedValues)[0])?.label : `${selectedValues.size} selected`;
  const isActive = selectedValues.size > 0;
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-152">
      <button onClick={() => setIsOpen(!isOpen)} className={`flex items-center justify-between w-full py-1.5 pl-2 pr-2 border rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-[#D44444] focus:border-[#D44444] transition-colors ${isActive ? 'border-[#D44444] bg-red-50 text-[#D44444] font-medium' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`} data-id="element-153">
        <div className="flex items-center truncate mr-2" data-id="element-154">
          {icon && <span className={`mr-1.5 flex-shrink-0 ${isActive ? 'text-[#D44444]' : 'text-gray-400'}`} data-id="element-155">
              {icon}
            </span>}
          <span className="truncate" data-id="element-156">{displayValue}</span>
        </div>
        <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-50" data-id="element-157" />
      </button>

      {isOpen && <div className="absolute z-50 w-48 mt-1 bg-white border border-gray-200 rounded-md shadow-lg py-1 max-h-60 overflow-auto right-0 md:right-auto md:left-0" data-id="element-158">
          {options.map(option => <div key={option.value} className="flex items-center px-3 py-1.5 hover:bg-gray-50 cursor-pointer" onClick={() => toggleOption(option.value)} data-id="element-159">
              <div className={`w-3.5 h-3.5 border rounded-sm mr-2 flex items-center justify-center flex-shrink-0 transition-colors ${selectedValues.has(option.value) ? 'bg-[#D44444] border-[#D44444]' : 'border-gray-300'}`} data-id="element-160">
                {selectedValues.has(option.value) && <Check className="w-2.5 h-2.5 text-white" data-id="element-161" />}
              </div>
              <span className="text-xs text-gray-700" data-id="element-162">{option.label}</span>
            </div>)}
        </div>}
    </div>;
}